import type { ReadonlyURLSearchParams } from "next/navigation";
import type { SearchParams } from "~www/types/search-params";

function getSearchParamsFromUrlSearchParams(
  urlSearchParams: URLSearchParams | ReadonlyURLSearchParams,
): SearchParams {
  const searchParams: SearchParams = {};

  for (const key of new Set(urlSearchParams.keys())) {
    const values = urlSearchParams.getAll(key);

    if (values.length === 0) {
      continue;
    }

    searchParams[key] = values.length === 1 ? values[0] : values;
  }

  return searchParams;
}

/**
 * Extracts a single string value from Next.js search params, returning the
 * first element if the value is an array. Returns undefined if the key is not
 * found or the value is explicitly undefined.
 */
function getSearchParam(
  searchParams: SearchParams | undefined,
  key: string,
): string | undefined {
  const value = searchParams?.[key];
  return Array.isArray(value) ? value[0] : value;
}

function appendSearchParamsToUrlSearchParams(
  params: URLSearchParams,
  searchParams: SearchParams | undefined,
  excludedKeys: string[] = [],
): URLSearchParams {
  Object.entries(searchParams ?? {}).forEach(([key, value]) => {
    if (value === undefined || excludedKeys.includes(key)) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((entry) => {
        params.append(key, entry);
      });
      return;
    }

    params.set(key, value);
  });

  return params;
}

export {
  appendSearchParamsToUrlSearchParams,
  getSearchParam,
  getSearchParamsFromUrlSearchParams,
};
